import { ErrorMessage } from "@atlaskit/form";
import { currencyFormatter } from "@fla/common";
import {
  AssetType,
  Button,
  ConsentOrdersPathName,
  CustomCheckbox,
  FormNavigation,
  SuperannuationBaseProps,
  SuperannuationBaseSchema,
  Superannuation_InitialState,
  useNames,
  useSuper
} from "@fla/components";
import SuperannuationIcon from "@fla/components/src/assets/svgs/superannuation.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "app/store";
import { withConsentOrder } from "components/HOCs/withConsentOrder";
import { useConsentOrders } from "hooks/ConsentOrders";
import { SliceStatus } from "interfaces";
import { selectAsset, selectConsentOrdersBase } from "pages/ConsentOrders";
import { consentOrdersActions } from "pages/ConsentOrders/consentOrdersSlice";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const defaultValues = {
  if_no_super: "no"
};

function Superannuation() {
  const { initialDetails } = useSelector(selectConsentOrdersBase);

  const superannuationData = useSelector(selectAsset(AssetType.SUPERANNUATION)) as Superannuation_InitialState[];
  const _useNames = useNames({ initialDetails });
  const { spouseFirstName } = _useNames;

  const [status, setStatus] = useState(SliceStatus.Idle);
  const [error, setError] = useState<string>();

  const location = useLocation();
  const navigate = useNavigate();
  const { control, handleSubmit, reset, getValues, watch } = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(SuperannuationBaseSchema)
  });

  const handleBackClick = () => {
    navigate(ConsentOrdersPathName.RealEstate);
  };

  const editData = (values?: SuperannuationBaseProps) => {
    navigate(`${ConsentOrdersPathName.Superannuation}/add`, {
      state: {
        parent: getValues(),
        child: values
      }
    });
  };

  const dispatch = useAppDispatch();
  const { updateAssetsDebts } = useConsentOrders();

  const onSubmit = async (values: SuperannuationBaseProps) => {
    try {
      if (values.if_no_super === "yes" && superannuationData.length) {
        setError("If neither of you have any super in Australia, then please remove the added superannuation details.");
      } else if (values.if_no_super !== "yes" && !superannuationData.length) {
        setError("please add superannuation details.");
      } else {
        setError("");
        setStatus(SliceStatus.Pending);
        const payload = {
          super: { data: values, isCompleted: true },
          currentStep: 3
        };
        await updateAssetsDebts(payload);
        dispatch(consentOrdersActions.setAssetsDebts(payload));
        setStatus(SliceStatus.Resolved);
        navigate(ConsentOrdersPathName.Bank);
      }
    } catch (error) {
      setStatus(SliceStatus.Rejected);
    }
  };

  useEffect(() => {
    if (location.state) {
      reset(location.state);
    } else {
      reset(defaultValues);
    }
  }, [location]);

  const { getCardFor, userTotalAmount, spouseTotalAmount } = useSuper({
    superannuationData,
    ..._useNames
  });

  return (
    <section className="w-full py-10">
      <form
        id="superannuation-form"
        className="md:w-10/12 lg:max-w-2xl mx-auto h-fit px-5 md:px-10 md:py-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <img src={SuperannuationIcon} className="w-10 h-auto mx-auto my-3" alt="superannuation" />

        <h3 className="text-center my-5">Superannuation</h3>

        <div className="w-full py-10 border border-gray-400 border-dashed rounded-lg flex flex-col items-center justify-center space-y-10 mb-5">
          <p className="px-16 text-center text-base font-bold">
            Please add all the all the super funds you and {spouseFirstName}
            &nbsp; have in Australia
          </p>
          <Button
            className="px-5 py-2 uppercase"
            fontSize="sm"
            onClick={() => editData(undefined)}
            disabled={watch("if_no_super") === "yes"}
          >
            + Add Super
          </Button>
        </div>

        <article className="mb-12">
          <CustomCheckbox
            control={control}
            name="if_no_super"
            title="Click if neither of you have any super in Australia"
            valueType="string"
            classes="w-fit mx-auto"
            hideErrorMsg
            onChange={() => setError("")}
          />
          {error ? (
            <div className="max-w-sm mx-auto text-center">
              <ErrorMessage>{error}</ErrorMessage>
            </div>
          ) : null}
        </article>

        {Array.isArray(superannuationData) && superannuationData.length ? (
          <>
            <p className="uppercase text-gray-500 text-xl font-medium my-5">You’re Keeping</p>

            {getCardFor("user", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(userTotalAmount)}</span>
              </p>
            </section>

            <p className="uppercase text-gray-500 text-xl font-medium my-5">{spouseFirstName} Keeping</p>

            {getCardFor("spouse", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(spouseTotalAmount)}</span>
              </p>
            </section>
          </>
        ) : null}

        <FormNavigation
          isFirst={false}
          isLast={false}
          form="superannuation-form"
          handleBackClick={handleBackClick}
          nextButtonText="Continue"
          isSubmitting={status === SliceStatus.Pending}
          classes="mt-12"
        />
      </form>
    </section>
  );
}
export default withConsentOrder(Superannuation);
